// ----------------------------------------------------------------------- imports externos
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useOutletContext } from "react-router-dom";

// ----------------------------------------------------------------------- imports internos
import AsignarOPrestar, { plazoInicial } from "../components/taller/AsignarOPrestar";
import AvisoWhatsapp from "../components/taller/AvisoWhatsapp";
import DibujoHerramienta from "../components/taller/DibujoHerramienta";
import FotosHerramienta from "../components/taller/FotosHerramienta";
import HerramientasDePersona from "../components/funcionales/HerramientasDePersona";
import PuertaCerrada from "../components/funcionales/PuertaCerrada";
import { buscarFila, useItemPedido } from "../hooks/useItemPedido";
import "../assets/css/herramientas.css";
import {
  FILTROS,
  altoDelTablero,
  cuando,
  COLUMNAS,
  cuentaPorPanol,
  devolver,
  estaPrestada,
  hayLibres,
  estadoDe,
  fechaLarga,
  filtrar,
  guardarPosiciones,
  haceCuanto,
  herramientasQueCoinciden,
  historialDe,
  idsDeLoQueTiene,
  loQueTiene as traerLoQueTiene,
  nombreDe,
  nombrePanol,
  ordenLoQueTiene,
  PANOLES,
  personas as traerPersonas,
  plazo,
  plazoDePrestamo,
  plazoElegido,
  prestar,
  resumen,
  SIN_PANOL,
  sinEncimar,
  soloDelPanol,
  unidades,
  tablero as traerTablero,
  valoresDe,
} from "../services/herramientas";

/* El tablero de herramientas.
 *
 * ── QUÉ TIENE QUE CONTESTAR EN DOS SEGUNDOS ──────────────────────────────────
 *
 * Igna, 14/09/2026: *«que una persona pueda mirar la pantalla durante 2
 * segundos y entender qué herramienta falta, quién la tiene y desde cuándo»*.
 * Todo lo de acá abajo sale de eso:
 *
 *   · la herramienta que está se DIBUJA, la que no está deja su SOMBRA;
 *   · debajo del hueco va quién la tiene y desde cuándo, no un ícono de estado;
 *   · lo que un filtro deja afuera se APAGA, no se saca — sacarlo movería las
 *     demás de lugar, y el lugar es lo que hace que se reconozca cuál falta.
 *
 * ── EL LUGAR ES EL DATO ──────────────────────────────────────────────────────
 *
 * Cada herramienta tiene una posición fija guardada en `taller_herramienta`
 * (x, y, ancho, alto en celdas de una grilla de 24). No se ordena por nombre
 * ni por estado: un tablero que se reordena solo deja de ser un tablero. Se
 * acomoda desde «Editar tablero», arrastrando.
 *
 * ── VER Y PRESTAR SON DOS PERMISOS ───────────────────────────────────────────
 *
 * `ver.taller` abre la pantalla; `cargar.taller` habilita prestar y devolver.
 * Sin el segundo el tablero se ve entero y los botones no están: tráfico puede
 * mirar qué falta sin poder mover nada.
 */

const ALTO_CELDA = 64; // px por fila de la grilla

/* ── LA FILA CRECE; LA CHAPA NO SE SALE DE SU LUGAR ──────────────────────────
 *
 * Nico, 20/10 («BUG», con captura): la chapa del taladro neumático salía por
 * arriba de su lugar, tapaba el destornillador plano y quedaba cortada por el
 * borde del tablero. La de las llaves Allen, demorada, también subía más que
 * las otras.
 *
 * Las filas medían 64 px fijos. Una herramienta de 2×2 tiene 138 px de alto,
 * y una chapa prestada —nombre, estado, cuántas, quién la tiene, desde
 * cuándo— más el piso del dibujo no entra. Como la chapa va pegada abajo, lo
 * que sobraba crecía PARA ARRIBA, sobre la herramienta de arriba, y lo que
 * salía del tablero lo cortaba el `overflow` de `.hrr-scroll`. (La chapa,
 * además, venía inflada: otra regla con el mismo nombre le ponía el apellido
 * en una píldora grande; ver `.hrr-destinatario` en herramientas.css. Y el
 * taladro estaba en el lugar del destornillador: ver `sinEncimar`.)
 *
 * Las filas miden 64 px COMO MÍNIMO y crecen cuando una chapa lo pide. En la
 * pared de todos los días ninguna lo pide (las de 4×4 tienen 286 px) y se ve
 * igual que antes; para que el dibujo no infle la fila, la figura no aporta
 * alto propio (ver `.hrr-figura` en herramientas.css). */
const filasDelTablero = (alto) => `repeat(${alto}, minmax(${ALTO_CELDA}px, auto))`;

/* ── EN EL TELÉFONO, EL TABLERO ES UNA LISTA EN DOS COLUMNAS ─────────────────
 *
 * Igna, 02/10: «el sector de herramientas sigue andando mal (al menos desde el
 * teléfono)». Visto en un iPhone (390 px): la pared mide 720 px fijos, así que
 * la mitad de las herramientas quedaba fuera de la pantalla, a la derecha,
 * adentro de una caja que había que arrastrar de costado mientras la página
 * se arrastra para abajo — y debajo, un corcho vacío del alto de la pared.
 *
 * La decisión anterior era «dejar correr a lo ancho antes que apilar», para no
 * perder el dónde de cada cosa. En la compu y en la tablet eso sigue igual. En
 * el teléfono gana lo otro: el que lo mira está buscando UNA herramienta, y
 * una que no entra en la pantalla no la encuentra. Se apilan en dos columnas
 * EN EL ORDEN DE LA PARED (de izquierda a derecha y de arriba abajo), que es
 * lo que queda del dónde cuando no entra el plano.
 *
 * Acomodar el tablero (arrastrar posiciones) no tiene sentido sobre una lista:
 * en el teléfono el botón no aparece, y se acomoda desde la compu o la tablet.
 */
const CELULAR = "(max-width: 640px)";

function useEsCelular() {
  const mirar = () =>
    typeof window !== "undefined" && !!window.matchMedia?.(CELULAR)?.matches;
  const [celu, setCelu] = useState(mirar);
  useEffect(() => {
    const mq = typeof window !== "undefined" && window.matchMedia?.(CELULAR);
    if (!mq) return undefined;
    const cambio = () => setCelu(!!mq.matches);
    mq.addEventListener?.("change", cambio);
    return () => mq.removeEventListener?.("change", cambio);
  }, []);
  return celu;
}

/* El lugar guardado en la base. `2` es lo que pone la base si no viene. */
const lugarGuardado = (h) => ({
  x: Number(h.x) || 0, y: Number(h.y) || 0,
  ancho: Number(h.ancho) || 2, alto: Number(h.alto) || 2,
});

/* El orden de la pared, para apilarla: fila por fila, de izquierda a derecha. */
function enOrdenDePared(filas, posDe) {
  return [...filas].sort((a, b) => {
    const pa = posDe(a); const pb = posDe(b);
    return pa.y - pb.y || pa.x - pb.x || String(a.nombre).localeCompare(String(b.nombre));
  });
}

/* ── Una posición del tablero ─────────────────────────────────────────────── */

function Posicion({ h, pos, apagada, editando, elegida, eligiendo,
                    onAbrir, onAgarrar }) {
  const est = estadoDe(h);
  const falta = estaPrestada(h);
  const tarde = String(h.estado).toUpperCase() === "DEMORADA";

  return (
    <button
      type="button"
      className={`hrr-pos ${est.clase}` +
        (falta ? " hrr-pos--falta" : "") +
        (apagada ? " hrr-pos--apagada" : "") +
        (editando ? " hrr-pos--mueble" : "") +
        (elegida ? " hrr-pos--elegida" : "") +
        (eligiendo && !hayLibres(h) ? " hrr-pos--nodisp" : "")}
      style={pos ? {
        gridColumn: `${pos.x + 1} / span ${pos.ancho}`,
        gridRow: `${pos.y + 1} / span ${pos.alto}`,
      } : undefined}
      onClick={() => { if (!editando) onAbrir(h); }}
      /* Para que el buscador maestro la encuentre (`?ver=<id>`, ver
         `useItemPedido`). */
      data-ver={h.id}
      aria-pressed={eligiendo ? !!elegida : undefined}
      onPointerDown={editando ? (e) => onAgarrar(e, h, "mover") : undefined}
      aria-label={`${h.nombre} — ${est.rotulo}` +
        (falta ? `, la tiene ${h.persona_nombre}` : "")}
    >
      {/* El tirador de la esquina cambia el TAMAÑO. Va sólo en modo edición:
          una esquina agarrable en el tablero de todos los días es una forma de
          desarmarlo sin querer. */}
      {elegida && <i className="hrr-tilde" aria-hidden="true">✓</i>}
      {editando && (
        <i
          className="hrr-medir"
          onPointerDown={(e) => onAgarrar(e, h, "medir")}
          aria-hidden="true"
        />
      )}
      {/* SE DIBUJA IGUAL ESTÉ O NO ESTÉ. La silueta gris suponía una
          herramienta por gancho, y no hay una de cada cosa: con tres llaves
          combinadas, apagar «la» posición porque salió una dice algo falso.
          Quién la tiene y desde cuándo lo cuenta la chapa, que es un dato. */}
      <span className="hrr-figura">
        <DibujoHerramienta tipo={h.tipo} />
      </span>

      <span className={`hrr-chapa${tarde ? " hrr-chapa--tarde" : ""}`}>
        <b>{h.nombre}</b>
        <span className="hrr-estado">
          <i className="hrr-punto" aria-hidden="true" />
          {est.rotulo}
        </span>
        {/* CUÁNTAS ESTÁN AFUERA, cuando hay más de una. Sin esto, cien
            guantes con tres prestados se ven idénticos a cien sin tocar: la
            chapa diría DISPONIBLE y nada más, que es cierto y no es lo que
            alguien va a buscar al tablero. */}
        {unidades(h) && <span className="hrr-desde">{unidades(h)}</span>}
        {falta && (
          <>
            <span className="hrr-quien">{h.persona_nombre}</span>
            <span className="hrr-desde">
              {cuando(h.retirado_en)} · {haceCuanto(h.horas_prestada)}
            </span>
          </>
        )}
      </span>
    </button>
  );
}

/* ── El panel de la derecha ───────────────────────────────────────────────── */

function Panel({ h, puedeEditar, onCerrar, onHecho }) {
  const [historial, setHistorial] = useState(undefined);
  const [gente, setGente] = useState([]);
  const [busca, setBusca] = useState("");
  const [elegida, setElegida] = useState(null);
  const [nota, setNota] = useState("");
  /* #262 (Nico): a qué unidad (tractor/furgón) va lo que se entrega. El pañol
     reparte repuestos para una unidad, no siempre para una persona a secas. */
  const [unidad, setUnidad] = useState("");
  /* ASIGNAR O PRESTAR (Nico, 06/10): por defecto Asignar —#272, «la mayoría
     no las presto, se las doy»—; Prestar pide días o fecha de devolución. */
  const [modo, setModo] = useState(plazoInicial);
  const [trabajando, setTrabajando] = useState(false);
  const [error, setError] = useState("");
  const [aviso, setAviso] = useState("");

  const est = estadoDe(h);
  /* `hayLibres` y no `estaLibre`: el estado describe la herramienta entera
     —cien guantes con uno demorado son DEMORADA— y lo que decide si este panel
     presta o devuelve es si queda alguna en el cajón. */
  const libre = hayLibres(h);

  useEffect(() => {
    let vivo = true;
    historialDe(h.id).then((r) => vivo && setHistorial(r.error ? null : r.filas));
    return () => { vivo = false; };
  }, [h.id]);

  /* El padrón se pide al servidor con lo que se tipeó y no se filtra una lista
     traída entera: son miles de personas y bajarlas todas para buscar tres
     letras es esperar por algo que no se va a mirar. */
  useEffect(() => {
    if (!libre || !puedeEditar) return undefined;
    let vivo = true;
    const t = setTimeout(() => {
      traerPersonas(busca).then((r) => vivo && setGente(r.filas));
    }, 200);
    return () => { vivo = false; clearTimeout(t); };
  }, [busca, libre, puedeEditar]);

  const accion = async () => {
    setError("");
    const pl = libre ? plazoElegido(modo) : null;
    if (pl && !pl.ok) { setError(pl.error); return; }
    setTrabajando(true);
    const r = libre
      ? await prestar({ herramientaId: h.id, persona: elegida, nota, unidad,
                        definitivo: pl.definitivo, venceEl: pl.vence })
      : await devolver({ prestamoId: h.prestamo_id, nota });
    setTrabajando(false);
    if (!r.ok) { setError(r.error); return; }
    // Salió, pero con algo que decir: se queda abierto hasta que lo lean.
    if (r.aviso) { setAviso(r.aviso); return; }
    onHecho();
  };

  const devolverUna = async (prestamoId) => {
    setError("");
    setTrabajando(true);
    const r = await devolver({ prestamoId, nota });
    setTrabajando(false);
    if (!r.ok) { setError(r.error); return; }
    onHecho();
  };

  return (
    <div className="hrr-fondo" onClick={onCerrar} role="presentation">
      <aside
        className="hrr-panel"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label={h.nombre}
      >
        <h2>{h.nombre}</h2>
        <div className="hrr-codigo">{h.codigo}</div>

        <div className="hrr-panel-foto">
          <DibujoHerramienta tipo={h.tipo} />
        </div>

        {/* LAS FOTOS, SI TIENE (P-348). Optativas: el dibujo de arriba es el
            del tablero y no cambia; las fotos son para reconocer ESTA. Sin
            fotos no se dibuja nada. Se cargan y se quitan desde la ficha
            («Administrar»). */}
        <FotosHerramienta herramientaId={h.id} nombre={h.nombre} soloVer />

        <div className="hrr-dato"><span>Estado</span><span>{est.rotulo}</span></div>
        {unidades(h) && (
          <div className="hrr-dato"><span>Unidades</span><span>{unidades(h)}</span></div>
        )}
        {h.categoria && (
          <div className="hrr-dato"><span>Categoría</span><span>{h.categoria}</span></div>
        )}
        {h.sector && (
          <div className="hrr-dato"><span>Sector</span><span>{h.sector}</span></div>
        )}
        {h.fuera_de_servicio && h.fuera_motivo && (
          <div className="hrr-dato">
            <span>Motivo</span><span>{h.fuera_motivo}</span>
          </div>
        )}
        {estaPrestada(h) && (
          <>
            <div className="hrr-dato">
              <span>{h.definitivo ? "Se la dieron a" : "La tiene"}</span>
              <span>{h.persona_nombre}</span>
            </div>
            {h.unidad && (
              <div className="hrr-dato">
                <span>Para</span><span>interno {h.unidad}</span>
              </div>
            )}
            <div className="hrr-dato">
              <span>{h.definitivo ? "Entregada" : "Desde"}</span>
              <span>{fechaLarga(h.retirado_en)}
                {h.definitivo ? " · queda para la persona" : ` · ${haceCuanto(h.horas_prestada)}`}</span>
            </div>
            {/* Una entrega definitiva no tiene plazo: no vuelve. */}
            {!h.definitivo && (
              <div className="hrr-dato"><span>Plazo</span><span>{plazo(h)}</span></div>
            )}
            {h.entrego_nombre && (
              <div className="hrr-dato">
                <span>Se la entregó</span><span>{h.entrego_nombre}</span>
              </div>
            )}
          </>
        )}
        {h.nota && (
          <div className="hrr-dato"><span>Nota</span><span>{h.nota}</span></div>
        )}

        {/* ── Prestar ─────────────────────────────────────────────────── */}
        {libre && puedeEditar && (
          <>
            <label className="hrr-campo">
              Quién se la lleva
              <input
                type="search"
                value={busca}
                placeholder="Buscar en el padrón…"
                onChange={(e) => setBusca(e.target.value)}
                aria-label="Buscar persona"
              />
            </label>
            <div className="hrr-lista">
              {gente.length === 0 && (
                <button type="button" disabled>
                  {busca ? "Nadie con ese nombre en el padrón" : "Escribí para buscar"}
                </button>
              )}
              {gente.map((p) => (
                <button
                  key={p.dni}
                  type="button"
                  aria-pressed={elegida?.dni === p.dni}
                  onClick={() => setElegida(p)}
                >
                  {nombreDe(p)}
                  <small>
                    DNI {p.dni}
                    {p.legajo ? ` · legajo ${p.legajo}` : ""}
                    {p.sector ? ` · ${p.sector}` : ""}
                  </small>
                </button>
              ))}
            </div>
            <label className="hrr-campo">
              Para (interno, opcional)
              <input
                type="text"
                value={unidad}
                placeholder="Tractor o furgón, ej. 010 o F010"
                onChange={(e) => setUnidad(e.target.value)}
                aria-label="Para qué unidad"
              />
            </label>
            {/* #272 + 06/10 (Nico): por defecto se ASIGNA; prestar pide plazo. */}
            <AsignarOPrestar valor={modo} onCambiar={setModo} />
            {elegida && (
              <AvisoWhatsapp dni={elegida.dni} nombre={nombreDe(elegida)}
                             prestar={modo.modo === "prestar"} />
            )}
          </>
        )}

        {puedeEditar && !h.fuera_de_servicio && (
          <label className="hrr-campo">
            Nota (opcional)
            <input
              type="text"
              value={nota}
              onChange={(e) => setNota(e.target.value)}
              aria-label="Nota"
            />
          </label>
        )}

        {error && <p className="hrr-aviso">{error}</p>}
        {aviso && <p className="hrr-aviso hrr-aviso-wa">{aviso}</p>}

        <div className="hrr-acciones">
          <button type="button" className="hrr-btn" onClick={aviso ? onHecho : onCerrar}>
            Cerrar
          </button>
          {/* Con la herramienta afuera Y dada en definitiva no hay «devolución»
              que registrar: se la dieron, no vuelve. */}
          {puedeEditar && !aviso && !h.fuera_de_servicio && (libre || !h.definitivo) && (
            <button
              type="button"
              className="hrr-btn hrr-btn--principal"
              disabled={trabajando || (libre && !elegida)}
              onClick={accion}
            >
              {trabajando ? "Guardando…"
                : libre ? (modo.modo === "prestar" ? "Prestar herramienta" : "Asignársela")
                        : "Registrar devolución"}
            </button>
          )}
        </div>

        {/* ── Historial ───────────────────────────────────────────────── */}
        <div className="hrr-historial">
          <h3>Últimos movimientos</h3>
          {historial === undefined && <p className="hrr-codigo">Buscando…</p>}
          {/* «No pude leer» no es «nunca se prestó»: son dos frases distintas y
              la segunda, dicha sobre una consulta caída, es una afirmación que
              nadie verificó. */}
          {historial === null && (
            <p className="hrr-codigo">No se pudo consultar.</p>
          )}
          {historial && historial.length === 0 && (
            <p className="hrr-codigo">Todavía no se prestó nunca.</p>
          )}
          {historial && historial.length > 0 && (
            <ul>
              {historial.map((p) => (
                <li key={p.id}>
                  <b>{p.persona_nombre}</b>
                  {p.unidad ? ` · interno ${p.unidad}` : ""}
                  {p.definitivo ? " · entregada (queda para la persona)" : ""}
                  <br />
                  <time>{fechaLarga(p.retirado_en)}</time>
                  {p.definitivo
                    ? ""
                    : p.devuelto_en
                      ? <> → <time>{fechaLarga(p.devuelto_en)}</time></>
                      : ` · sin devolver${p.vence_el ? ` · ${plazoDePrestamo(p)}` : ""}`}
                  {/* SE DEVUELVE LA DE ESTA PERSONA, no «la» de la herramienta.
                      Con una sola unidad daba igual; con veinte afuera, un
                      botón único cerraría siempre el préstamo más viejo y le
                      descontaría a cualquiera la que trajo otro. Una ENTREGA
                      DEFINITIVA no se devuelve: no lleva botón. */}
                  {!p.devuelto_en && !p.definitivo && puedeEditar && (
                    <>
                      {" "}
                      <button type="button" className="hrr-btn"
                              disabled={trabajando}
                              onClick={() => devolverUna(p.id)}>
                        Devolver
                      </button>
                    </>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </aside>
    </div>
  );
}

/* ── Prestar varias de una ────────────────────────────────────────────────── */

/* Nico, 14/09: *«estaría bueno poder asignar más de una herramienta a la
 * vez»*. Igna propuso separarlas por coma.
 *
 * POR COMA EN WHATSAPP, ELIGIENDO ACÁ. En un mensaje de texto la coma es la
 * única forma de escribir una lista, y así quedó del lado del bot. En una
 * pantalla que YA MUESTRA las herramientas dibujadas, pedirle a alguien que
 * tipee sus nombres es hacerle escribir lo que tiene enfrente — y darle una
 * forma nueva de equivocarse: «amoladora» mal escrita no existe, y con la
 * lista a la vista eso no puede pasar.
 */
function PanelVarias({ herramientas, onCerrar, onHecho }) {
  const [gente, setGente] = useState([]);
  const [busca, setBusca] = useState("");
  const [elegida, setElegida] = useState(null);
  const [nota, setNota] = useState("");
  const [unidad, setUnidad] = useState(""); // #262: a qué interno va la entrega
  const [modo, setModo] = useState(plazoInicial); // 06/10: Asignar por defecto
  const [trabajando, setTrabajando] = useState(false);
  const [error, setError] = useState("");
  const [aviso, setAviso] = useState("");
  const [fallidas, setFallidas] = useState([]);
  const prestando = modo.modo === "prestar";

  useEffect(() => {
    let vivo = true;
    const t = setTimeout(() => {
      traerPersonas(busca).then((r) => vivo && setGente(r.filas));
    }, 200);
    return () => { vivo = false; clearTimeout(t); };
  }, [busca]);

  const prestarTodas = async () => {
    setError("");
    setFallidas([]);
    const pl = plazoElegido(modo);
    if (!pl.ok) { setError(pl.error); return; }
    setTrabajando(true);
    const r = await prestar({
      herramientaIds: herramientas.map((h) => h.id),
      persona: elegida, nota, unidad, definitivo: pl.definitivo, venceEl: pl.vence });
    setTrabajando(false);
    if (!r.ok) { setError(r.error); return; }
    if (r.aviso) setAviso(r.aviso);
    /* LO QUE NO SE PUDO SE DICE, y no se cierra el panel como si hubiera
       salido todo. Anotar cuatro de cinco y contestar «listo» es la forma de
       que la quinta aparezca perdida dentro de un mes. */
    if (r.fallidas?.length) {
      setFallidas(r.fallidas.map((f) => ({
        ...f,
        nombre: herramientas.find((h) => h.id === f.id)?.nombre || f.id,
      })));
      return;
    }
    if (r.aviso) return;
    onHecho();
  };

  const nombres = herramientas.map((h) => h.nombre).join(", ");

  return (
    <div className="hrr-fondo" onClick={onCerrar} role="presentation">
      <aside className="hrr-panel" onClick={(e) => e.stopPropagation()}
             role="dialog" aria-label="Prestar varias herramientas">
        <h2>{prestando ? "Prestar" : "Asignar"} {herramientas.length} herramientas</h2>
        <div className="hrr-codigo">{nombres}</div>

        <label className="hrr-campo">
          Quién se las lleva
          <input type="search" value={busca} aria-label="Buscar persona"
                 placeholder="Buscar en el padrón…"
                 onChange={(e) => setBusca(e.target.value)} />
        </label>
        <div className="hrr-lista">
          {gente.length === 0 && (
            <button type="button" disabled>
              {busca ? "Nadie con ese nombre en el padrón" : "Escribí para buscar"}
            </button>
          )}
          {gente.map((p) => (
            <button key={p.dni} type="button"
                    aria-pressed={elegida?.dni === p.dni}
                    onClick={() => setElegida(p)}>
              {nombreDe(p)}
              <small>
                DNI {p.dni}{p.sector ? ` · ${p.sector}` : ""}
              </small>
            </button>
          ))}
        </div>

        <label className="hrr-campo">
          Para (interno, opcional)
          <input type="text" value={unidad} aria-label="Para qué unidad"
                 placeholder="Tractor o furgón, ej. 010 o F010"
                 onChange={(e) => setUnidad(e.target.value)} />
        </label>

        <label className="hrr-campo">
          Nota (opcional)
          <input type="text" value={nota} aria-label="Nota"
                 onChange={(e) => setNota(e.target.value)} />
        </label>

        {/* #272 + 06/10: por defecto se ASIGNAN; prestar pide plazo. */}
        <AsignarOPrestar valor={modo} onCambiar={setModo} plural />
        {elegida && (
          <AvisoWhatsapp dni={elegida.dni} nombre={nombreDe(elegida)} prestar={prestando} />
        )}

        {error && <p className="hrr-aviso">{error}</p>}
        {aviso && <p className="hrr-aviso hrr-aviso-wa">{aviso}</p>}
        {fallidas.length > 0 && (
          <div className="hrr-aviso">
            No pude anotar {fallidas.length} de {herramientas.length}:
            <ul>
              {fallidas.map((f) => (
                <li key={f.id}>{f.nombre} — {f.error}</li>
              ))}
            </ul>
            El resto quedó anotado.
          </div>
        )}

        <div className="hrr-acciones">
          <button type="button" className="hrr-btn" onClick={aviso ? onHecho : onCerrar}>
            {fallidas.length || aviso ? "Listo" : "Cancelar"}
          </button>
          {!fallidas.length && !aviso && (
            <button type="button" className="hrr-btn hrr-btn--principal"
                    disabled={trabajando || !elegida} onClick={prestarTodas}>
              {trabajando ? "Guardando…"
                : prestando ? `Prestar las ${herramientas.length}`
                            : `Asignárselas (${herramientas.length})`}
            </button>
          )}
        </div>
      </aside>
    </div>
  );
}

/* ── Buscar herramienta o persona ─────────────────────────────────────────────
 *
 * Nico, 01/10, tres pedidos que son uno:
 *
 *   1. «que se empiece a rellenar» — a las dos letras se despliega quién y qué
 *      coincide, y se elige con el mouse, el dedo o ↑↓ + Enter;
 *   2. «que sea exacta: si pongo Insaurralde y hay más de uno, que me deje
 *      elegir» — las personas salen del padrón, una por renglón con su DNI y
 *      sector, y elegida una el tablero muestra SÓLO lo suyo, por DNI;
 *   3. «lo que tiene asignado, que se ponga primero» — elegida la persona,
 *      arriba del tablero va lo que tiene, lo asignado adelante.
 *
 * LA PARED NO SE REORDENA. «Primero» no puede ser mover las herramientas de
 * lugar: el lugar es lo que hace que se reconozca cuál falta (ver arriba). Lo
 * suyo se pone ADELANTE del tablero, en una lista, y en la pared se prende.
 *
 * Reemplaza al plegable «Ver la caja de una persona» (#272), que hacía lo
 * mismo escondido en otro campo: el que buscaba a alguien escribía en el
 * buscador de siempre, y ése apagaba la pared en vez de contestar.
 *
 * Escribir y no elegir sigue funcionando como antes —el texto apaga lo que no
 * coincide—, para «¿dónde está la amoladora?» sin pasar por la lista.
 */
function Buscador({ texto, onTexto, filas, onPersona, onHerramienta }) {
  const [gente, setGente] = useState([]);
  const [abierto, setAbierto] = useState(false);
  const [marcado, setMarcado] = useState(0);

  useEffect(() => {
    const t = String(texto || "").trim();
    if (t.length < 2) { setGente([]); return undefined; }
    let vivo = true;
    const timer = setTimeout(() => {
      traerPersonas(t).then((r) => vivo && setGente((r.filas || []).slice(0, 8)));
    }, 150);
    return () => { vivo = false; clearTimeout(timer); };
  }, [texto]);

  const cosas = useMemo(() => herramientasQueCoinciden(filas, texto), [filas, texto]);
  const opciones = useMemo(() => [
    ...gente.map((p) => ({ tipo: "persona", p })),
    ...cosas.map((h) => ({ tipo: "herramienta", h })),
  ], [gente, cosas]);

  useEffect(() => { setMarcado(0); }, [texto]);

  const elegir = (o) => {
    setAbierto(false);
    if (o.tipo === "persona") onPersona(o.p);
    else onHerramienta(o.h);
  };

  const tecla = (e) => {
    if (!opciones.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault(); setAbierto(true);
      setMarcado((m) => Math.min(m + 1, opciones.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault(); setMarcado((m) => Math.max(m - 1, 0));
    } else if (e.key === "Enter" && abierto) {
      e.preventDefault(); elegir(opciones[marcado] || opciones[0]);
    } else if (e.key === "Escape") {
      setAbierto(false);
    }
  };

  const mostrar = abierto && String(texto || "").trim().length >= 2;

  return (
    <div className="hrr-buscador">
      <input
        className="hrr-buscar"
        type="search"
        value={texto}
        placeholder="Buscar herramienta o persona… (ej. Pére)"
        onChange={(e) => { onTexto(e.target.value); setAbierto(true); }}
        onFocus={() => setAbierto(true)}
        onBlur={() => setTimeout(() => setAbierto(false), 150)}
        onKeyDown={tecla}
        aria-label="Buscar herramienta o persona"
        aria-autocomplete="list"
        aria-expanded={mostrar && opciones.length > 0}
        role="combobox"
        aria-controls="hrr-sugerencias"
      />
      {mostrar && opciones.length > 0 && (
        <div className="hrr-lista hrr-sugerencias" id="hrr-sugerencias"
             role="listbox">
          {opciones.map((o, i) => (
            <button
              key={o.tipo === "persona" ? `p${o.p.dni}` : `h${o.h.id}`}
              type="button"
              role="option"
              aria-selected={i === marcado}
              /* `onMouseDown` y no `onClick`: el `blur` del campo llega antes
                 que el clic y cerraría la lista con el dedo encima. */
              onMouseDown={(e) => { e.preventDefault(); elegir(o); }}
            >
              {o.tipo === "persona" ? (
                <>
                  👤 {nombreDe(o.p)}
                  <small>
                    DNI {o.p.dni}
                    {o.p.legajo ? ` · legajo ${o.p.legajo}` : ""}
                    {o.p.sector ? ` · ${o.p.sector}` : ""}
                  </small>
                </>
              ) : (
                <>
                  🧰 {o.h.nombre}
                  <small>{o.h.codigo}{o.h.estado ? ` · ${estadoDe(o.h).rotulo}` : ""}</small>
                </>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* Lo que tiene la persona elegida, ADELANTE del tablero. Cada renglón abre la
 * herramienta como si se la tocara en la pared. */
function LoQueTiene({ persona, prestamos, error, filas, onAbrir, onQuitar }) {
  const { asignadas, prestadas } = ordenLoQueTiene(prestamos || []);
  const filaDe = (p) => (filas || []).find((f) => f.id === p.herramienta_id);
  const nombre = (p) =>
    p.herramienta?.nombre || filaDe(p)?.nombre || p.herramienta?.codigo || "Herramienta";

  const renglon = (p, asignada) => (
    <li key={p.id}>
      <button type="button" className="hrr-tiene-item"
              onClick={() => { const f = filaDe(p); if (f) onAbrir(f); }}
              disabled={!filaDe(p)}>
        <b>{nombre(p)}</b>
        <small>
          {asignada ? "asignada · es suya" : "prestada"}
          {p.retirado_en ? ` · desde ${cuando(p.retirado_en)}` : ""}
          {!asignada && p.vence_el ? ` · ${plazoDePrestamo(p)}` : ""}
        </small>
      </button>
    </li>
  );

  return (
    <section className="hrr-tiene" aria-label="Lo que tiene la persona">
      <p className="hrr-tiene-quien">
        👤 <b>{nombreDe(persona)}</b>
        <small>DNI {persona.dni}{persona.sector ? ` · ${persona.sector}` : ""}</small>
        <button type="button" className="hrr-btn" onClick={onQuitar}>
          ✕ Ver todo el tablero
        </button>
      </p>
      {prestamos === undefined && <p className="tenue">Consultando…</p>}
      {error && (
        <p className="hrr-aviso">No se pudo consultar.</p>
      )}
      {prestamos && !error && !asignadas.length && !prestadas.length && (
        <p className="tenue">No tiene ninguna herramienta del tablero ahora.</p>
      )}
      {asignadas.length > 0 && (
        <>
          <h4>Asignado ({asignadas.length})</h4>
          <ul>{asignadas.map((p) => renglon(p, true))}</ul>
        </>
      )}
      {prestadas.length > 0 && (
        <>
          <h4>Prestado ({prestadas.length})</h4>
          <ul>{prestadas.map((p) => renglon(p, false))}</ul>
        </>
      )}
      <details className="hrr-tiene-historial">
        <summary>Ver todo su historial</summary>
        <HerramientasDePersona dni={persona.dni} />
      </details>
    </section>
  );
}

/* ── La pantalla ──────────────────────────────────────────────────────────── */

const Herramientas = () => {
  const { permisos = null } = useOutletContext() || {};
  const permitido = permisos !== null && permisos.includes("ver.taller");
  const puedeEditar = permisos !== null && permisos.includes("cargar.taller");

  const [todasFilas, setFilas] = useState(undefined); // undefined = cargando
  const [error, setError] = useState("");

  /* ── CADA PAÑOL, SU PARED (P-311) ──────────────────────────────────────
   *
   * Nico, 05/10: *«las herramientas de préstamos debe diferenciar entre
   * pañoles ya que en [algunos] tengo herramientas específicas»*. Cada
   * herramienta del catálogo sabe de qué pañol es (`panol`), y arriba se
   * elige cuál mirar. Lo de otro pañol se SACA de la pared, no se apaga:
   * apagado se leería como «está, pero prestada». Arranca en todos, que es
   * lo que se veía hasta hoy; `?panol=puerto` abre directo en uno. */
  const { search } = useLocation();
  const [panolElegido, setPanolElegido] = useState(
    () => new URLSearchParams(search).get("panol") || "");
  const filas = useMemo(
    () => (todasFilas === undefined ? undefined : soloDelPanol(todasFilas, panolElegido)),
    [todasFilas, panolElegido]);
  const porPanol = useMemo(() => cuentaPorPanol(todasFilas || []), [todasFilas]);
  const [texto, setTexto] = useState("");
  const [estado, setEstado] = useState("todas");
  const [categoria, setCategoria] = useState("");
  const [sector, setSector] = useState("");
  const [abierta, setAbierta] = useState(null);

  // La persona elegida en el buscador, y lo que tiene (por DNI).
  // `undefined` = consultando; el error se dice aparte.
  const [persona, setPersona] = useState(null);
  const [suyos, setSuyos] = useState(undefined);
  const [errorSuyos, setErrorSuyos] = useState("");

  // Elegir varias para prestarlas juntas. Es un MODO y no un ctrl+clic: esto
  // se usa parado en el taller, muchas veces desde una tablet, y un atajo de
  // teclado ahí no existe.
  const [eligiendo, setEligiendo] = useState(false);
  const [elegidas, setElegidas] = useState([]);

  // El modo «acomodar la pared». Lo que se arrastra vive en `movidas` hasta
  // que se aprieta Guardar (ver el bloque de más abajo).
  const tableroRef = useRef(null);
  const [editando, setEditando] = useState(false);
  const celu = useEsCelular();
  const [movidas, setMovidas] = useState({});
  const [guardando, setGuardando] = useState(false);
  const arrastre = useRef(null);

  /* ── LA HERRAMIENTA QUE PIDIÓ EL BUSCADOR ────────────────────────────────
   *
   * Igna, 02/10: «que se abra en el ítem exacto, no solo la pantalla». El
   * buscador maestro manda acá con `?ver=<id de taller_herramienta>`: caer en
   * el pañol con veintiséis herramientas a la vista y tener que buscar otra
   * vez la que ya se eligió es hacer dos veces la misma búsqueda.
   *
   * ABRIR ES LO MISMO QUE TOCARLA: se centra su lugar en la pared, titila, y
   * se abre su panel —estado, quién la tiene, historial—, que es lo que
   * alguien quiere saber cuando busca una herramienta por nombre.
   *
   * SI UN FILTRO LA TAPA, SE SACAN LOS FILTROS. Al entrar están todos en
   * «todas», pero el pedido puede llegar con la pantalla ya abierta y la pared
   * apagada por una búsqueda o una persona elegida; en el teléfono, además,
   * lo apagado ni se dibuja. Pedirla y que no aparezca sería contestar «no
   * está» sobre algo que está.
   *
   * SI NO ESTÁ, SE DICE. La vista del tablero trae sólo las activas: una que
   * se dio de baja después de que el buscador la listara no tiene lugar en la
   * pared, y abrir la pantalla sin decir nada parecería que el link no anduvo.
   * Con el tablero caído no se afirma eso: ya hay un aviso que dice que no se
   * pudo leer, y «no la encontré» sobre una lectura fallida sería inventar. */
  const { ver, resaltar, listo } = useItemPedido();
  const [noEsta, setNoEsta] = useState("");

  const cargar = useCallback(async () => {
    const r = await traerTablero();
    setFilas(r.filas);
    setError(r.error);
  }, []);

  useEffect(() => { if (permitido) cargar(); }, [permitido, cargar]);

  useEffect(() => {
    if (!persona) { setSuyos(undefined); setErrorSuyos(""); return undefined; }
    let vivo = true;
    setSuyos(undefined);
    traerLoQueTiene(persona.dni).then((r) => {
      if (!vivo) return;
      setSuyos(r.filas || []);
      setErrorSuyos(r.error || "");
    });
    return () => { vivo = false; };
  }, [persona, filas]);

  const cuenta = useMemo(() => resumen(filas || []), [filas]);
  const visibles = useMemo(() => {
    // Con una persona elegida, se prende EXACTO lo suyo: por sus préstamos,
    // no por el nombre que muestra la pared (que puede ser el de otro).
    const base = filtrar(filas || [], { texto: persona ? "" : texto, estado,
                                        categoria, sector });
    if (!persona) return new Set(base.map((f) => f.id));
    const suyas = idsDeLoQueTiene(suyos || []);
    return new Set(base.filter((f) => suyas.has(f.id)).map((f) => f.id));
  }, [filas, texto, estado, categoria, sector, persona, suyos]);

  useEffect(() => {
    if (!ver || filas === undefined) return;
    const h = filas.find((f) => String(f.id) === ver);
    // Es de otro pañol: se vuelve a «todos» y se la busca de nuevo.
    if (!h && panolElegido && (todasFilas || []).some((f) => String(f.id) === ver)) {
      setPanolElegido("");
      return;
    }
    if (!h) {
      setNoEsta(error ? "" : ver);
      listo();
      return;
    }
    setNoEsta("");
    const hayFiltro = texto || estado !== "todas" || categoria || sector || persona;
    if (!visibles.has(h.id) && hayFiltro) {
      setTexto(""); setEstado("todas"); setCategoria(""); setSector("");
      setPersona(null);
      return; // vuelve a correr con la pared entera prendida
    }
    resaltar(buscarFila(ver, tableroRef.current));
    // Acomodando la pared o eligiendo varias, tocar no abre: el pedido tampoco.
    if (!editando && !eligiendo) setAbierta(h);
    listo();
  }, [ver, filas, todasFilas, panolElegido, error, visibles, texto, estado,
      categoria, sector, persona, editando, eligiendo, resaltar, listo]);

  /* Las que caen sobre el lugar de otra (un alta que no dice dónde va queda en
     el 0,0) se muestran abajo de todo. Ver `sinEncimar`. Se calcula sobre lo
     GUARDADO y no sobre lo que se está arrastrando: si no, soltar una encima
     de otra mandaría a la otra al fondo en el medio del arrastre. */
  const corridas = useMemo(
    () => sinEncimar((filas || []).map((f) => ({ id: f.id, ...lugarGuardado(f) }))),
    [filas]);
  const posDe = (h) => movidas[h.id] || corridas[h.id] || lugarGuardado(h);
  // Las que siguen abajo esperando que alguien las acomode.
  const sinAcomodar = Object.keys(corridas).filter((id) => !movidas[id]).length;

  const alto = useMemo(
    () => altoDelTablero((filas || []).map(
      (f) => movidas[f.id] || corridas[f.id] || lugarGuardado(f))),
    [filas, movidas, corridas]);

  /* ── Acomodar el tablero ─────────────────────────────────────────────────
   *
   * Igna: *«que el tablero tenga un modo edición tipo drag & drop. Como
   * administrador agarrás una herramienta y literalmente la movés hasta donde
   * querés que quede»* — para armar el tablero de cada taller sin programar
   * posiciones.
   *
   * SE ARRASTRA EN LA PANTALLA Y SE GUARDA AL FINAL. Escribir en cada
   * movimiento llenaría la base de estados intermedios que nadie quiso
   * guardar, y dejaría el tablero a medio acomodar si alguien cierra la
   * pestaña en el medio. Mientras tanto vive en `movidas`, y Cancelar lo tira.
   *
   * PUNTEROS Y NO HTML5 DRAG. `pointerdown/move/up` anda igual con el mouse y
   * con el dedo; el arrastre de HTML5 no existe en un celular, y el tablero se
   * va a acomodar tanto desde el monitor del taller como desde una tablet.
   */
  // En el teléfono, la pared se apila (ver `useEsCelular`). Si alguien entró
  // a acomodarla desde una pantalla grande y la achicó, se le deja terminar.
  const enLista = celu && !editando;
  // Y en la lista, lo que no coincide con la búsqueda (o no es de la persona
  // elegida) no se dibuja apagado: se saca. En la pared apagar sirve, porque
  // el lugar vacío dice dónde está cada cosa; en una lista de dos columnas lo
  // apagado sólo empuja lo que se busca hacia abajo.
  const soloLoQueSeBusca = enLista && !eligiendo
    ? (filas || []).filter((f) => visibles.has(f.id))
    : (filas || []);

  const agarrar = (e, h, modo) => {
    const caja = tableroRef.current?.getBoundingClientRect();
    if (!caja) return;
    e.preventDefault();
    e.stopPropagation();
    arrastre.current = {
      id: h.id, modo, base: posDe(h), x0: e.clientX, y0: e.clientY,
      /* El tamaño de celda sale de dividir el tablero por sus columnas y sus
         filas. Es una aproximación —no descuenta el padding ni los espacios
         entre celdas— y alcanza: lo que se guarda es un número ENTERO de
         celdas, así que unos píxeles de error no cambian dónde encaja. */
      celdaW: caja.width / COLUMNAS,
      celdaH: caja.height / Math.max(alto, 1),
    };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };

  const mover = (e) => {
    const a = arrastre.current;
    if (!a) return;
    const dCol = Math.round((e.clientX - a.x0) / a.celdaW);
    const dFila = Math.round((e.clientY - a.y0) / a.celdaH);
    const p = { ...a.base };
    if (a.modo === "mover") {
      // No se puede sacar del tablero: fuera de la grilla no se dibuja, y una
      // herramienta que desaparece al arrastrarla parece una que se borró.
      p.x = Math.min(Math.max(0, a.base.x + dCol), COLUMNAS - a.base.ancho);
      p.y = Math.max(0, a.base.y + dFila);
    } else {
      p.ancho = Math.min(Math.max(1, a.base.ancho + dCol), COLUMNAS - a.base.x);
      p.alto = Math.max(1, a.base.alto + dFila);
    }
    setMovidas((m) => ({ ...m, [a.id]: p }));
  };

  const soltar = () => { arrastre.current = null; };

  const guardarTablero = async () => {
    setGuardando(true);
    const r = await guardarPosiciones(movidas);
    setGuardando(false);
    if (!r.ok) { setError(r.error); return; }
    setMovidas({});
    setEditando(false);
    cargar();
  };

  const salirDeEdicion = () => { setMovidas({}); setEditando(false); };

  /* SÓLO SE PUEDEN ELEGIR LAS QUE ESTÁN. Tocar una prestada en este modo no
     hace nada: agregarla a la lista para que después falle al guardar sería
     dejar que alguien arme una entrega que ya sabemos que no se puede. */
  const tocarParaElegir = (h) => {
    if (!hayLibres(h)) return;
    setElegidas((xs) => xs.includes(h.id)
      ? xs.filter((i) => i !== h.id)
      : [...xs, h.id]);
  };

  const salirDeElegir = () => { setElegidas([]); setEligiendo(false); };

  if (!permitido) {
    return <PuertaCerrada />;
  }

  const tocar = (clave) => setEstado((e) => (e === clave ? "todas" : clave));

  return (
    <div className="hrr">
      <p className="hrr-caja-volver">
        <Link to="/taller/herramientas">← Cajas de herramientas</Link>
      </p>
      <div className="hrr-cabecera">
        <h1 className="hrr-titulo">
          Pañol · préstamos{panolElegido ? ` · ${nombrePanol(panolElegido)}` : ""}
        </h1>
        <div className="hrr-cuenta">
          <button type="button" className="hrr-ind hrr-ind--ok"
                  aria-pressed={estado === "DISPONIBLE"}
                  onClick={() => tocar("DISPONIBLE")}>
            <b>{cuenta.disponibles}</b> <small>disponibles</small>
          </button>
          <button type="button" className="hrr-ind hrr-ind--uso"
                  aria-pressed={estado === "PRESTADA"}
                  onClick={() => tocar("PRESTADA")}>
            <b>{cuenta.prestadas + cuenta.demoradas}</b> <small>en uso</small>
          </button>
          <button type="button" className="hrr-ind hrr-ind--tarde"
                  aria-pressed={estado === "DEMORADA"}
                  onClick={() => tocar("DEMORADA")}>
            <b>{cuenta.demoradas}</b> <small>demoradas</small>
          </button>
          <button type="button" className="hrr-ind"
                  aria-pressed={estado === "todas"}
                  onClick={() => setEstado("todas")}>
            <b>{cuenta.total}</b> <small>total</small>
          </button>
        </div>
      </div>

      {todasFilas && todasFilas.length > 0 && (
        <div className="hrr-quienes" role="group" aria-label="Pañol">
          <button type="button" className="hrr-chip" aria-pressed={!panolElegido}
                  onClick={() => setPanolElegido("")}>
            Todos los pañoles ({porPanol[""] || 0})
          </button>
          {PANOLES.map((p) => (
            <button key={p.id} type="button" className="hrr-chip"
                    aria-pressed={panolElegido === p.id}
                    onClick={() => setPanolElegido(p.id)}>
              {p.corto} ({porPanol[p.id] || 0})
            </button>
          ))}
          {porPanol[SIN_PANOL] > 0 && (
            <button type="button" className="hrr-chip"
                    aria-pressed={panolElegido === SIN_PANOL}
                    onClick={() => setPanolElegido(SIN_PANOL)}>
              Sin pañol ({porPanol[SIN_PANOL]})
            </button>
          )}
        </div>
      )}

      {puedeEditar && (
        <div className="hrr-barra-edicion">
          {!editando && !eligiendo && (
            <button type="button" className="hrr-btn"
                    onClick={() => setEligiendo(true)}>
              🧰 Prestar varias
            </button>
          )}
          {eligiendo && (
            <>
              <span className="hrr-ayuda">
                Tocá las herramientas que se lleva la misma persona.
                {elegidas.length ? ` Elegidas: ${elegidas.length}.` : ""}
              </span>
              <button type="button" className="hrr-btn" onClick={salirDeElegir}>
                Cancelar
              </button>
              <button type="button" className="hrr-btn hrr-btn--principal"
                      disabled={!elegidas.length}
                      onClick={() => setAbierta({ varias: true })}>
                Prestar {elegidas.length || ""}
              </button>
            </>
          )}
          {!editando && !eligiendo && !celu && (
            <button type="button" className="hrr-btn"
                    onClick={() => setEditando(true)}>
              ✋ Editar tablero
            </button>
          )}
          {/* DESDE ACÁ SE PODÍA TODO MENOS SUMAR UNA HERRAMIENTA.
              El alta vive en otra pantalla —el padrón— y a esta pantalla no la
              nombraba nadie: el que miraba el tablero y quería agregar una
              apretaba «Editar tablero», que es lo único que suena a eso, y ahí
              sólo se arrastra. Del padrón para acá había link; de acá para
              allá, ninguno. Una puerta de una sola mano es una pared.
              Va con `?nueva=1`, así el que la aprieta cae con la ficha abierta
              y no en otra tabla con otro botón. */}
          {!editando && !eligiendo && (
            <Link className="hrr-btn"
                  to="/taller/herramientas/administrar?nueva=1">
              ➕ Agregar herramienta
            </Link>
          )}
          {editando && (
            <>
              <span className="hrr-ayuda">
                Arrastrá cada herramienta a su lugar. La esquina de abajo a la
                derecha cambia el tamaño.
                {/* Las que no tenían lugar propio están abajo de todo: si no se
                    dice, quien acomoda la pared no sabe que tiene que bajar a
                    buscarlas. */}
                {sinAcomodar === 1 &&
                  " Una herramienta estaba en el lugar de otra y quedó abajo de todo: arrastrala a donde va."}
                {sinAcomodar > 1 &&
                  ` ${sinAcomodar} herramientas estaban en el lugar de otras y quedaron abajo de todo: arrastralas a donde van.`}
              </span>
              <button type="button" className="hrr-btn"
                      onClick={salirDeEdicion} disabled={guardando}>
                Cancelar
              </button>
              <button type="button" className="hrr-btn hrr-btn--principal"
                      onClick={guardarTablero}
                      disabled={guardando || !Object.keys(movidas).length}>
                {guardando ? "Guardando…"
                           : `Guardar ${Object.keys(movidas).length || ""}`.trim()}
              </button>
            </>
          )}
        </div>
      )}

      <div className="hrr-filtros">
        <Buscador
          texto={texto}
          onTexto={(t) => { setTexto(t); if (persona) setPersona(null); }}
          filas={filas || []}
          onPersona={(p) => { setPersona(p); setTexto(""); }}
          onHerramienta={(h) => { setPersona(null); setTexto(h.nombre); }}
        />
        <select value={estado} onChange={(e) => setEstado(e.target.value)}
                aria-label="Estado">
          {FILTROS.map(([v, r]) => <option key={v} value={v}>{r}</option>)}
        </select>
        <select value={categoria} onChange={(e) => setCategoria(e.target.value)}
                aria-label="Categoría">
          <option value="">Toda categoría</option>
          {valoresDe(filas || [], "categoria").map((c) =>
            <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={sector} onChange={(e) => setSector(e.target.value)}
                aria-label="Sector">
          <option value="">Todo sector</option>
          {valoresDe(filas || [], "sector").map((s) =>
            <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {persona && (
        <LoQueTiene
          persona={persona}
          prestamos={suyos}
          error={errorSuyos}
          filas={filas || []}
          onAbrir={setAbierta}
          onQuitar={() => setPersona(null)}
        />
      )}

      {/* Una consulta caída NO se dibuja como un tablero vacío: son la misma
          imagen y significan lo contrario. */}
      {error && (
        <p className="hrr-aviso">No se pudo consultar.</p>
      )}

      {noEsta && (
        <p className="hrr-aviso" role="status">
          No encontré esa herramienta (puede estar dada de baja).
        </p>
      )}

      {filas === undefined && <p className="hrr-vacio">Cargando el tablero…</p>}

      {filas && filas.length === 0 && !error && (
        <div className="hrr-vacio">
          <h3>{panolElegido ? `${nombrePanol(panolElegido)} no tiene herramientas` : "El tablero está vacío"}</h3>
          <p>
            {panolElegido
              ? "Ninguna herramienta del catálogo es de este pañol. El pañol de cada una se elige en Herramientas → Catálogo."
              : "Todavía no hay ninguna herramienta cargada. Se dan de alta en Taller → Herramientas → Administrar."}
          </p>
        </div>
      )}

      {enLista && filas && filas.length > 0 && soloLoQueSeBusca.length === 0 && (
        <p className="hrr-vacio">No hay herramientas que coincidan.</p>
      )}

      {filas && filas.length > 0 && !(enLista && soloLoQueSeBusca.length === 0) && (
        <div className={`hrr-scroll${enLista ? " hrr-scroll--celu" : ""}`}>
          <div
            ref={tableroRef}
            className={`hrr-tablero${editando ? " hrr-tablero--editando" : ""}` +
              (enLista ? " hrr-tablero--celu" : "")}
            style={enLista ? undefined
                           : { gridTemplateRows: filasDelTablero(alto) }}
            onPointerMove={editando ? mover : undefined}
            onPointerUp={editando ? soltar : undefined}
            onPointerCancel={editando ? soltar : undefined}
          >
            {(enLista ? enOrdenDePared(soloLoQueSeBusca, posDe) : filas).map((h) => (
              <Posicion
                key={h.id}
                h={h}
                pos={enLista ? null : posDe(h)}
                /* En modo edición no se apaga nada: se está acomodando la
                   pared entera y hay que verla entera. */
                apagada={!editando && !visibles.has(h.id)}
                editando={editando}
                elegida={elegidas.includes(h.id)}
                eligiendo={eligiendo}
                onAbrir={eligiendo ? tocarParaElegir : setAbierta}
                onAgarrar={agarrar}
              />
            ))}
          </div>
        </div>
      )}

      {abierta?.varias && (
        <PanelVarias
          herramientas={(filas || []).filter((f) => elegidas.includes(f.id))}
          onCerrar={() => { setAbierta(null); salirDeElegir(); cargar(); }}
          onHecho={() => { setAbierta(null); salirDeElegir(); cargar(); }}
        />
      )}

      {abierta && !abierta.varias && (
        <Panel
          h={filas.find((f) => f.id === abierta.id) || abierta}
          puedeEditar={puedeEditar}
          onCerrar={() => setAbierta(null)}
          onHecho={() => { setAbierta(null); cargar(); }}
        />
      )}
    </div>
  );
};

export default Herramientas;
export { Posicion, Panel };
